'use client';

import type { AdData, Adrop, AdropEvents } from '@adrop/ads-web-sdk';
import { captureException } from '@sentry/nextjs';
import { debounce } from 'es-toolkit';
import { useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import {
  AD_SLOT_DEBUG_PLACEHOLDER_ENABLED,
  ADROP_IOS_APP_MIN_VERSION,
} from '@/constants/ad';
import { ENV } from '@/constants/env';
import { useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';
import { isAppVersionAtLeast } from '@/utils';

import { useCustomRouter } from './useCustomRouter';

//* 광고 로드 상태 타입 (SDK AdropEvents 상수에서 유도, import type이라 런타임 import 없음)
type AdEventState = Extract<
  (typeof AdropEvents)[keyof typeof AdropEvents],
  'adReceived' | 'adFailed' | 'adNoFill' | 'adBackfillNoFill'
>;

export type AdLoadState = 'idle' | 'loading' | AdEventState;

//* adNoFill은 숨기지 않는다 — 직광고 no-fill 시 SDK가 백필을 그리는 진입 신호라서
//* 여기서 영역을 제거하면 백필이 노출 전에 죽는다. 백필까지 없으면 adBackfillNoFill.
export const isAdHidden = (state: AdLoadState) =>
  state === 'adFailed' || state === 'adBackfillNoFill';

const AD_DISABLED = false;
export const AD_MOCK_MODE = false;

let initialized = false;

type AdType = 'native' | 'banner';

const isInternalDomain = (url: string) => {
  try {
    const { hostname } = new URL(url);

    return (
      hostname.endsWith('welfarehello.com') ||
      hostname === 'localhost' ||
      hostname.startsWith('localhost:')
    );
  } catch {
    return false;
  }
};

//* destinationURL에서 실제 목적지 URL 추출 (redirect 파라미터)
const extractDestinationUrl = (trackingUrl: string): string => {
  try {
    const url = new URL(trackingUrl);
    const redirect = url.searchParams.get('redirect');

    return redirect ?? trackingUrl;
  } catch {
    return trackingUrl;
  }
};

//* 광고 목적지로 이동해도 되는 URL인지 판정한다.
//* 광고 소재가 destinationURL(또는 redirect 파라미터)에 `tripcom://`·`intent://` 같은 앱 딥링크를
//* 실어 보내면, 웹뷰에서는 앱이 그 링크를 받아 외부 앱을 띄운다(앱 진입만으로 트립닷컴·클룩이
//* 열리던 원인). 앱 쪽에도 스킴 화이트리스트를 뒀지만, 웹에서 애초에 넘기지 않는 게 2차 방어다.
//* 상대경로(`/`)는 내부 라우팅이므로 호출부에서 먼저 처리한다.
export const isOpenableAdDestination = (url: string) =>
  /^https?:\/\//i.test(url);

export const useAd = (unitId: string, options: { type?: AdType } = {}) => {
  const { type = 'native' } = options;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [adState, setAdState] = useState<AdLoadState>('idle');
  const router = useCustomRouter();

  //* 브레이크포인트 전환 등으로 unitId가 바뀌면 이전 유닛의 결과(adNoFill 등)가
  //* 새 유닛에 남지 않도록 같은 렌더에서 초기화한다 (render-phase derived state reset)
  const [loadedUnitId, setLoadedUnitId] = useState(unitId);
  if (loadedUnitId !== unitId) {
    setLoadedUnitId(unitId);
    setAdState('idle');
  }

  const { isMounted, webviewType, osType, appVersion } = useDeviceInfoStore(
    useShallow((state) => ({
      isMounted: state.isMounted,
      webviewType: state.webviewType,
      osType: state.osType,
      appVersion: state.appVersion,
    })),
  );
  const isExternalWebview =
    webviewType === WEBVIEW_TYPE.KB_PAY || webviewType === WEBVIEW_TYPE.TOSS;
  //* iframe about:blank를 막아 광고 칸이 하얗게 비던 iOS 앱 가드는 ADROP_IOS_APP_MIN_VERSION에서 고쳐졌다
  const isIosWelloApp =
    osType === 'ios' &&
    webviewType === WEBVIEW_TYPE.WELLO_APP &&
    !(appVersion && isAppVersionAtLeast(appVersion, ADROP_IOS_APP_MIN_VERSION));
  const isDebugPlaceholderEnabled =
    AD_SLOT_DEBUG_PLACEHOLDER_ENABLED && !AD_MOCK_MODE;
  const isAdLoadDisabled =
    isDebugPlaceholderEnabled || isExternalWebview || isIosWelloApp;

  useEffect(() => {
    //* isMounted가 true가 된 후에만 webviewType 확정됨
    if (!isMounted) return;

    //* dev에선 Adrop이 광고를 안 내려주므로 SDK 로드를 건너뛰고 adFailed로 통일 →
    //* 모든 슬롯이 "개발용 광고 영역" placeholder로 표시됨 (mock 테스트 중이면 예외).
    //* 외부 웹뷰(KB Pay, Toss) 또는 iOS 웰로 앱에서는 광고 비활성화 → fallback 표시
    if (isAdLoadDisabled) return;

    if (AD_DISABLED || !containerRef.current) return;

    let cancelled = false;
    let adrop: Adrop | null = null;

    //* 상대경로/절대경로 분기 처리 (debounce로 중복 클릭 방지)
    const handleAdClick = debounce(
      (_unit: string, adData: AdData) => {
        const trackingUrl = adData.destinationURL;
        if (!trackingUrl) return;

        const url = extractDestinationUrl(trackingUrl);

        if (url.startsWith('/')) {
          router.push(url);

          return;
        }

        //* http(s)가 아닌 목적지(앱 딥링크·intent 등)는 열지 않는다 — 위 판정 함수 주석 참조
        if (!isOpenableAdDestination(url)) {
          captureException(new Error('허용되지 않은 광고 목적지 스킴'), {
            extra: { url: url.slice(0, 80) },
          });

          return;
        }

        //* 내부 도메인은 전체 새로고침 대신 SPA 라우팅으로 이동
        if (isInternalDomain(url)) {
          const { pathname, search, hash } = new URL(url);
          router.push(pathname + search + hash);

          return;
        }

        window.open(url, '_blank');
      },
      300,
      { edges: ['leading'] },
    );

    const handlers = {
      onReceived: () => setAdState('adReceived'),
      onFailed: () => setAdState('adFailed'),
      onNoFill: () => setAdState('adNoFill'),
      onBackfillNoFill: () => setAdState('adBackfillNoFill'),
      onClicked: handleAdClick,
    };

    const setup = async () => {
      setAdState('loading');

      try {
        const { Adrop: AdropClass, AdropEvents } =
          await import('@adrop/ads-web-sdk');

        if (cancelled) return;

        if (!initialized) {
          AdropClass.observe({
            appId: ENV.NEXT_PUBLIC_ADROP_APP_ID,
            debug: false,
            //* 모든 슬롯이 adPlacements 고정 사이즈 frame(inline width/height·aspect-ratio) 안에
            //* 렌더되므로 responsive 자동 예약 대신 fixed로 컨테이너 크기에 광고를 맞춘다 (SDK 1.2.3+)
            backfillMode: 'fixed',
          });
          initialized = true;
        }

        adrop = AdropClass.instance();

        if (!adrop || cancelled) {
          if (!adrop) setAdState('adFailed');

          return;
        }

        const filter = { unit: unitId };
        adrop.on(AdropEvents.AD_RECEIVED, handlers.onReceived, filter);
        adrop.on(AdropEvents.AD_FAILED, handlers.onFailed, filter);
        adrop.on(AdropEvents.AD_NO_FILL, handlers.onNoFill, filter);
        adrop.on(AdropEvents.AD_CLICKED, handlers.onClicked, filter);
        adrop.on(
          AdropEvents.AD_BACKFILL_NO_FILL,
          handlers.onBackfillNoFill,
          filter,
        );

        //* renderAd로 useCustomClick 활성화 (기본값 false이므로 반드시 호출 필요)
        await adrop.renderAd(containerRef.current!, {
          unit: unitId,
          useCustomClick: true,
          //* native 전용 옵션
          ...(type === 'native' && {
            trackMode: 1,
            isEntireClick: true,
          }),
        });
      } catch {
        setAdState('adFailed');
      }
    };

    setup();

    return () => {
      cancelled = true;
      if (adrop) {
        adrop.off('adReceived', handlers.onReceived);
        adrop.off('adFailed', handlers.onFailed);
        adrop.off('adNoFill', handlers.onNoFill);
        adrop.off('adClicked', handlers.onClicked);
        adrop.off('adBackfillNoFill', handlers.onBackfillNoFill);
      }
    };
  }, [unitId, type, isMounted, isAdLoadDisabled, router]);

  return {
    containerRef,
    adState: isMounted && isAdLoadDisabled ? 'adFailed' : adState,
  };
};
